import { Glue42Core } from "@glue42/core";

import {Glue42Office} from "../../../glue4office";
import {generateConversationId, isOutlookEnabled} from "../helpers/index";
import {
    CRMGetItemAsMsgMethodName,
    OutlookGetItemAsMsgMethodName,
    SaveItemMethodName,
    ShowItemMethodName,
    SyncTrackEmailMethodName,
    SyncUntrackEmailMethodName,
} from "../helpers/methodNames";
import {validateId} from "../helpers/validations";
import getItemAsMsgDictionary from "../dictionaries/GetItemAsMsgDictionary";
import trackEmailDictionary from "../dictionaries/TrackEmailDictionary";

export class Manager {
    private agm: Glue42Core.AGM.API;

    public init(agm: Glue42Core.AGM.API): void {
        this.agm = agm;
    }

    public showItemMethod(ids: Glue42Office.Outlook.T42Id[], methodName: string): Promise<Glue42Office.Outlook.T42Id[]> {
        return new Promise((resolve, reject) => {
            if (!isOutlookEnabled(this.agm)) {
                reject(`The method "${methodName}" is not available`);
                return;
            }

            ids = this.validateIds(ids);

            const successHandler = () => {
                resolve(ids);
            };

            const errorHandler = (err: { message: string }) => {
                reject(err.message);
                return;
            };

            this.agm.invoke(ShowItemMethodName, {itemT42Ids: ids})
                .then(() => successHandler())
                .catch((err: any) => errorHandler(err));
        });
    }

    public saveItemMethod(ids: Glue42Office.Outlook.T42Id[], methodName: string): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!isOutlookEnabled(this.agm)) {
                reject(`The method "${methodName}" is not available`);
                return;
            }

            const successHandler = (r: { returned: any }) => {
                resolve(r.returned.url);
            };

            const errorHandler = (err: { message: string }) => {
                reject(err.message);
                return;
            };

            this.agm.invoke(SaveItemMethodName, {itemT42Ids: ids})
                .then((r: { returned: any }) => successHandler(r))
                .catch((err: any) => errorHandler(err));
        });
    }

    public getItemAsMsg(ids: Glue42Office.Outlook.T42Id[]): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!isOutlookEnabled(this.agm)) {
                reject(`The method "getAsMsg" is not available`);
                return;
            }

            const cookie: string = new Date().getTime().toString();

            const successHandler = () => {
                getItemAsMsgDictionary.add(cookie, {resolve, reject});
            };

            const errorHandler = (err: { message: string }) => {
                reject(err.message);
                return;
            };

            const getItemOptions = {
                itemIds: ids,
                callback: OutlookGetItemAsMsgMethodName,
                cookie,
            };

            this.agm.invoke(CRMGetItemAsMsgMethodName, getItemOptions)
                .then(() => successHandler())
                .catch((err: any) => errorHandler(err));
        });
    }

    public handleTrackingEmails(action: string,
                                emailIds: Glue42Office.Outlook.T42Id[],
                                conversationId?: Glue42Office.Outlook.T42Id): Promise<{ emailIds: Glue42Office.Outlook.T42Id[], conversationIds: Glue42Office.Outlook.T42Id[] }> {
        return new Promise((resolve, reject) => {
            if (!isOutlookEnabled(this.agm)) {
                reject(`The method "${action}" is not available`);
                return;
            }

            const id = conversationId ? validateId(conversationId) : null;
            const isTrackEmail: boolean = action === "track";
            const conversationIds = this.combineConversationIds(emailIds, id, isTrackEmail);

            const successHandler = () => {
                resolve({emailIds, conversationIds});

                if (!isTrackEmail) {
                    trackEmailDictionary.remove(emailIds);
                    const untrackIds = emailIds.filter((ids: Glue42Office.Outlook.T42Id) => ids.systemName === ("Outlook.ConversationId"));
                    trackEmailDictionary.add(emailIds, untrackIds);
                }
            };

            const errorHandler = (err: { message: string }) => {
                reject(err.message);
                return;
            };

            const methodName = isTrackEmail ? SyncTrackEmailMethodName : SyncUntrackEmailMethodName;
            const params = {emailIds, conversationIds};
            return this.agm.invoke(methodName, params)
                .then(successHandler)
                .catch(errorHandler);
        });
    }

    private validateIds(ids: Glue42Office.Outlook.T42Id[]): Glue42Office.Outlook.T42Id[] {
        if (!ids) {
            throw new Error(`"ids" are mandatory`);
        }
        if (!Array.isArray(ids) || ids.length === 0) {
            throw new Error("Invalid type of ids");
        }
        return ids.map((id: Glue42Office.Outlook.T42Id) => validateId(id));
    }

    private combineConversationIds(ids: Glue42Office.Outlook.T42Id[], conversationId: Glue42Office.Outlook.T42Id, isTrackEmail: boolean) {
        const conversationIds = trackEmailDictionary.containsIds(ids) ? trackEmailDictionary.get(ids) : [];

        if (isTrackEmail) {
            const newConversationIds = conversationId ? conversationId : generateConversationId();
            conversationIds.push(newConversationIds);

            if (!trackEmailDictionary.containsIds(ids)) {
                trackEmailDictionary.add(ids, conversationIds);
            }
        }
        return conversationIds;
    }
}

export default new Manager();
